/**
 * Product compare table — comparison grid (product-compare-table).
 */
(function () {
	'use strict';

	const productCompare = window.ProductCompare;

	if (!productCompare?.store || customElements.get('product-compare-table')) {
		return;
	}

	const {
		CHANGE_EVENT: compareChangeEvent,
		store: productCompareStore,
		compareToast,
		validateCompareProductType,
		getHandlesFromUrl,
		syncHandlesToUrl,
		syncStoreFromPayloads,
		fetchProductPayload,
		parseNutritionRows,
		buildUnionRows,
		htmlToPlainText,
		escapeHtml,
		escapeHtmlAttr,
		escapeCompareUrl,
	} = productCompare;

	class ProductCompareTable extends HTMLElement {
		initialized = false;
		#compareFetchController = null;
		#lastRenderedHandles = '';
		/** @type {{ payloads: CompareProductPayload[], parsedNutrition: Array<{ rows: unknown[], valuesByLabelKey: Record<string, string> }>, unionRows: Array<{ key: string, label: string, isIndented: boolean }> } | null} */
		#lastRenderState = null;

		constructor() {
			super();

			this.boundPageStoreChange = () => {
				if (!this.isConnected || this.mode !== 'page') {
					return;
				}

				const handles = productCompareStore.getHandles();
				const handlesKey = handles.join(',');

				if (handlesKey === this.#lastRenderedHandles) {
					return;
				}

				if (handles.length >= 2) {
					void this.#renderComparison(handles.slice(0, this.maxProducts));
				}
			};

			this.boundHandleHighlightChange = this.#handleHighlightChange.bind(this);
			this.boundHandleRunClick = this.#handleRunClick.bind(this);
			this.boundHandleSelectChange = this.#handleSelectChange.bind(this);
			this.boundHandleTraySync = this.#handleTraySync.bind(this);
			this.boundHandleCompareRun = this.#handleCompareRun.bind(this);
		}

		connectedCallback() {
			if (this.initialized) {
				return;
			}

			this.initialized = true;

			this.cacheElements();
			this.readConfig();
			this.bindEvents();
			this.init();
		}

		disconnectedCallback() {
			this.destroy();
			this.initialized = false;
		}

		readConfig() {
			this.helperSectionId = this.dataset.helperSection || 'helper-product-compare-data';
			this.maxProducts = parseInt(this.dataset.maxProducts || '3', 10);
			productCompareStore.maxProducts = this.maxProducts;
			this.mode = this.dataset.mode || 'product';
			this.missingValue = this.dataset.missingValue || '—';
			this.currentHandle = this.dataset.currentHandle || '';
			this.showIngredients = this.dataset.showIngredients !== 'false';

			if (this.highlightToggle) {
				this.highlightDifferences = this.highlightToggle.checked;
			} else {
				this.highlightDifferences = this.dataset.highlightDifferences !== 'false';
			}
		}

		cacheElements() {
			this.controlsEl = this.querySelector('[data-compare-controls]');
			this.statusEl = this.querySelector('[data-compare-status]');
			this.viewportEl = this.querySelector('[data-compare-viewport]');
			this.selectEl = this.querySelector('[data-compare-slot-select]');
			this.runBtn = this.querySelector('[data-compare-run]');
			this.toolbarEl = this.querySelector('[data-compare-toolbar]');
			this.highlightToggle = this.querySelector('[data-compare-highlight-toggle]');
		}

		bindEvents() {
			this.highlightToggle?.addEventListener('change', this.boundHandleHighlightChange);
			this.runBtn?.addEventListener('click', this.boundHandleRunClick);
			this.selectEl?.addEventListener('change', this.boundHandleSelectChange);
			document.addEventListener(compareChangeEvent, this.boundHandleTraySync);

			if (this.mode === 'page') {
				document.addEventListener(compareChangeEvent, this.boundPageStoreChange);
			}

			this.addEventListener('compare:run', this.boundHandleCompareRun);
		}

		init() {
			if (this.mode === 'page') {
				void this.#bootstrapPageMode();
			} else {
				if (this.controlsEl) {
					this.controlsEl.hidden = false;
				}

				this.#syncDynamicOptions();
				this.#bootstrapFromStore();
			}

			if (this.mode === 'product' && window.location.hash === '#product-compare') {
				void this.#runCompareFromStore();
			}
		}

		destroy() {
			this.#abortPendingFetches();
			this.highlightToggle?.removeEventListener('change', this.boundHandleHighlightChange);
			this.runBtn?.removeEventListener('click', this.boundHandleRunClick);
			this.selectEl?.removeEventListener('change', this.boundHandleSelectChange);
			document.removeEventListener(compareChangeEvent, this.boundHandleTraySync);
			document.removeEventListener(compareChangeEvent, this.boundPageStoreChange);
			this.removeEventListener('compare:run', this.boundHandleCompareRun);
		}

		#abortPendingFetches() {
			this.#compareFetchController?.abort();
			this.#compareFetchController = null;
		}

		#handleHighlightChange() {
			if (!this.highlightToggle) {
				return;
			}

			this.highlightDifferences = this.highlightToggle.checked;
			this.#rerenderLastTable();
		}

		#handleRunClick() {
			void this.#runCompare();
		}

		#handleSelectChange() {
			this.#updateRunButtonState();
		}

		#handleTraySync() {
			if (this.mode === 'page') {
				return;
			}

			this.#syncDynamicOptions();
		}

		#handleCompareRun() {
			if (this.mode === 'page') {
				void this.#bootstrapPageMode();
			} else {
				void this.#runCompareFromStore();
			}
		}

		#updateRunButtonState() {
			if (!this.runBtn) {
				return;
			}

			this.runBtn.disabled = !this.selectEl?.value;
		}

		async #bootstrapPageMode() {
			let handles = getHandlesFromUrl();

			if (!handles.length) {
				handles = productCompareStore.getHandles();
			}

			if (handles.length >= 2) {
				await this.#renderComparison(handles.slice(0, this.maxProducts));
			} else {
				this.#setStatus(this.dataset.pageEmptyMessage || this.dataset.emptyMessage || '');
				this.viewportEl?.setAttribute('hidden', '');
				this.#setToolbarVisible(false);
			}
		}

		#syncDynamicOptions() {
			if (!this.selectEl || !this.selectEl.hasAttribute('data-compare-dynamic-options')) {
				return;
			}

			const currentValue = this.selectEl.value;
			const entries = productCompareStore.getEntries().filter((entry) => entry.handle !== this.currentHandle);

			this.selectEl.innerHTML = `<option value="">${this.dataset.selectProductLabel || ''}</option>`;

			for (const entry of entries) {
				const option = document.createElement('option');

				option.value = entry.handle;
				option.textContent = entry.title;
				this.selectEl.appendChild(option);
			}

			if (entries.some((entry) => entry.handle === currentValue)) {
				this.selectEl.value = currentValue;
			}

			const hasOptions = entries.length > 0;

			this.selectEl.disabled = !hasOptions;

			if (this.runBtn) {
				this.runBtn.disabled = !this.selectEl.value;
			}

			if (this.statusEl && !hasOptions && !this.viewportEl?.innerHTML) {
				this.statusEl.textContent = this.dataset.trayEmptyHint || '';
			} else if (this.statusEl && hasOptions && this.statusEl.textContent === this.dataset.trayEmptyHint) {
				this.statusEl.textContent = '';
			}
		}

		#bootstrapFromStore() {
			if (!this.currentHandle) {
				return;
			}

			const otherHandles = productCompareStore.getHandles().filter((handle) => handle !== this.currentHandle);

			if (otherHandles.length > 0) {
				this.#runCompareFromStore();
			}
		}

		async #runCompare() {
			const handles = [this.currentHandle].filter(Boolean);
			const selected = this.selectEl?.value;

			if (selected && !handles.includes(selected)) {
				handles.push(selected);
			}

			const storeHandles = productCompareStore.getHandles().filter((handle) => handle !== this.currentHandle);

			for (const handle of storeHandles) {
				if (!handles.includes(handle) && handles.length < this.maxProducts) {
					handles.push(handle);
				}
			}

			await this.#renderComparison(handles.slice(0, this.maxProducts));
		}

		async #runCompareFromStore() {
			const handles = productCompareStore.getHandles();

			if (!handles.length) {
				return;
			}

			const merged = [this.currentHandle, ...handles.filter((handle) => handle !== this.currentHandle)].filter(Boolean);

			await this.#renderComparison(merged.slice(0, this.maxProducts));
		}

		async #renderComparison(handles) {
			if (handles.length < 2) {
				this.#setStatus(this.dataset.emptyMessage || '');

				return;
			}

			this.#abortPendingFetches();

			const fetchController = new AbortController();
			const { signal } = fetchController;

			this.#compareFetchController = fetchController;

			this.#setStatus(this.dataset.loadingMessage || '');
			this.viewportEl?.setAttribute('hidden', '');
			this.#setToolbarVisible(false);

			try {
				const payloads = await Promise.all(handles.map((handle) => fetchProductPayload(handle, this.helperSectionId, { signal })));

				if (!this.isConnected || signal.aborted) {
					return;
				}

				if (!validateCompareProductType(handles, payloads)) {
					compareToast('error', 'compare_toast_mixed_type', 'Selected products do not share the same product type.');
					this.#setStatus(
						this.dataset.mixedTypeMessage || this.dataset.errorMessage || 'Selected products do not share the same product type.',
					);
					this.viewportEl?.setAttribute('hidden', '');
					this.#setToolbarVisible(false);

					return;
				}

				const parsedNutrition = payloads.map((payload) => parseNutritionRows(payload.nutritionHtml || ''));
				const unionRows = buildUnionRows(parsedNutrition);

				this.#renderTable(payloads, parsedNutrition, unionRows);
				this.#lastRenderedHandles = handles.join(',');

				syncStoreFromPayloads(payloads);

				if (this.mode === 'page') {
					syncHandlesToUrl(handles);
				}

				this.#setStatus('');
				this.viewportEl?.removeAttribute('hidden');
				this.#setToolbarVisible(this.mode === 'page');
				this.#updateRunButtonState();
			} catch (error) {
				if (error?.name === 'AbortError') {
					return;
				}

				console.error('Product compare error:', error);
				this.#setStatus(this.dataset.errorMessage || '');
				this.#setToolbarVisible(false);
			} finally {
				if (this.#compareFetchController === fetchController) {
					this.#compareFetchController = null;
				}
			}
		}

		#rerenderLastTable() {
			if (!this.#lastRenderState) {
				return;
			}

			const { payloads, parsedNutrition, unionRows } = this.#lastRenderState;

			this.#renderTable(payloads, parsedNutrition, unionRows);
		}

		/** @param {boolean} visible */
		#setToolbarVisible(visible) {
			if (!this.toolbarEl) {
				return;
			}

			this.toolbarEl.hidden = !visible;
		}

		/**
		 * @param {CompareProductPayload[]} payloads
		 * @param {Array<{ rows: unknown[], valuesByLabelKey: Record<string, string> }>} parsedNutrition
		 * @param {Array<{ key: string, label: string, isIndented: boolean }>} unionRows
		 */
		#renderTable(payloads, parsedNutrition, unionRows) {
			if (!this.viewportEl) {
				return;
			}

			const colCount = payloads.length + 1;
			let html = `<div class="product-compare-table__scroll"><table class="product-compare-table__grid" style="--compare-cols:${colCount}">`;

			html += '<thead><tr>';
			html += '<th scope="col" class="product-compare-table__label-col"></th>';

			for (const payload of payloads) {
				const productUrl = escapeCompareUrl(payload.url, payload.handle);
				const safeTitle = escapeHtml(payload.title);
				const imageMarkup = payload.image
					? `<a href="${productUrl}"><img src="${escapeHtmlAttr(payload.image)}" alt="" width="120" height="120" loading="lazy"></a>`
					: '';

				html += `<th scope="col" class="product-compare-table__product-col">
          <div class="product-compare-table__product-head">
            ${imageMarkup}
            <a href="${productUrl}" class="product-compare-table__product-title">${safeTitle}</a>
            <div class="product-compare-table__product-price">${payload.priceHtml || ''}</div>
          </div>
        </th>`;
			}

			html += '</tr></thead><tbody>';

			const costPerServingLabel = this.dataset.costPerServingLabel || '';
			const costPerServingValues = payloads.map((payload) =>
				payload.costPerServing != null ? String(payload.costPerServing) : this.missingValue,
			);
			const costPerServingDiff =
				this.highlightDifferences && !costPerServingValues.every((value) => value === costPerServingValues[0])
					? ' product-compare-table__row--diff'
					: '';

			html += `<tr class="product-compare-table__row product-compare-table__row--cost-per-serving${costPerServingDiff}">`;
			html += `<th scope="row" class="product-compare-table__label-col">${escapeHtml(costPerServingLabel)}</th>`;

			for (const payload of payloads) {
				const cellMarkup = payload.costPerServingHtml ? payload.costPerServingHtml : escapeHtml(this.missingValue);

				html += `<td class="product-compare-table__value-col product-compare-table__value-col--cost-per-serving">${cellMarkup}</td>`;
			}

			html += '</tr>';

			for (const row of unionRows) {
				const values = parsedNutrition.map((nutritionParseResult) => nutritionParseResult.valuesByLabelKey[row.key] || this.missingValue);
				const allSame = values.every((value) => value === values[0]);
				const diffClass = this.highlightDifferences && !allSame ? ' product-compare-table__row--diff' : '';

				html += `<tr class="product-compare-table__row${diffClass}">`;
				html += `<th scope="row" class="product-compare-table__label-col${row.isIndented ? ' product-compare-table__label-col--indent' : ''}">${escapeHtml(row.label)}</th>`;

				for (const value of values) {
					html += `<td class="product-compare-table__value-col${row.isIndented ? ' product-compare-table__value-col--indent' : ''}">${escapeHtml(value)}</td>`;
				}

				html += '</tr>';
			}

			if (this.showIngredients) {
				const ingredientsLabel = this.dataset.ingredientsLabel || 'Ingredients';
				const values = payloads.map((payload) => {
					const text = htmlToPlainText(payload.ingredientsHtml || '');

					return text || this.missingValue;
				});
				const allSame = values.every((value) => value === values[0]);
				const diffClass = this.highlightDifferences && !allSame ? ' product-compare-table__row--diff' : '';

				html += `<tr class="product-compare-table__row product-compare-table__row--ingredients${diffClass}">`;
				html += `<th scope="row" class="product-compare-table__label-col">${escapeHtml(ingredientsLabel)}</th>`;

				for (const value of values) {
					const safeValue = escapeHtml(value).replace(/\n/g, '<br>');

					html += `<td class="product-compare-table__value-col product-compare-table__value-col--ingredients">${safeValue}</td>`;
				}

				html += '</tr>';
			}

			html += '</tbody></table></div>';
			this.viewportEl.innerHTML = html;
			this.#lastRenderState = { payloads, parsedNutrition, unionRows };
		}

		/** @param {string} message */
		#setStatus(message) {
			if (this.statusEl) {
				this.statusEl.textContent = message;
			}
		}
	}

	customElements.define('product-compare-table', ProductCompareTable);
})();
